import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { makeStyles } from '@material-ui/core';
import tw from 'twin.macro';
import { useEffect } from 'react';
import Filter from '../../../../Filters/Filter';
import Dropdown from '../../../../Dropdown';
import RateCodeConfigPreferences from '../RateCodePreferencesFilter/RateCodeConfigPreferences';
import usePreferencesFilter from '../../../../../hooks/usePreferencesHook';
import { setHotelTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { updateHotelSearchObj } from '../../../../../store/slices/hotel';
import { HotelPreferences } from '../../../../../constants';
import ContentSourceFilter from '../ContentSourceFilter';
import useContentSourceFilter from '../../../../../hooks/useContentSourceFilter';

const AgentPreferencesFilter = ({ tripId, variant, filterBgColor }) => {
  const isHotelTripSearch = !tripId;
  const hotelSearchObj = useSelector((store) => (!isHotelTripSearch
    ? (store.hotel.hotelSearches[tripId] || {}).searchObj || {}
    : store.tripSearch.searchInputs.hotel));
  const { travelAgency = {} } = useSelector((store) => store.user);
  const doReDesignRateCodesFilter = travelAgency && travelAgency.filterSettings
  && travelAgency.filterSettings.doRedesignRateCodesFilter;
  const { doTakeHotelContentSourceInput } = useContentSourceFilter(tripId);

  const {
    handlePreferencesChange, handlePreferencesClear,
    agentPreferencesLabel, isAgentPreferencesEmpty, preferences,
    selectedPreferences,
  } = usePreferencesFilter({ tripId, doShowInPreferences: true });

  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });

  const useFormStyle = makeStyles(() => ({
    root: {
      '&': tw`flex-nowrap px-4 w-full`,
    },
  }));

  const formClasses = useFormStyle();
  const dispatch = useDispatch();

  useEffect(() => {
    // useEFfect for setting default values on page load
    const defaultSelected = [];
    if (travelAgency && travelAgency.filterSettings) {
      const payload = {
        tripId,
        searchObj: {
          page: 1,
        },
      };
      if (travelAgency.filterSettings.doShowOnlyPreferredRates) {
        if (!doReDesignRateCodesFilter) {
          defaultSelected.push(HotelPreferences.ONLY_PREFERED_PARTNER_RATES);
        }
        if (hotelSearchObj?.exactMatchRateCodesOnly === undefined) {
          payload.searchObj.exactMatchRateCodesOnly = true;
        }
      } else if (doReDesignRateCodesFilter) {
        defaultSelected.push(HotelPreferences.BEST_AVAILABLE_RATES);
      }
      if (travelAgency.filterSettings.doShowCommissionableRatesOnly) {
        defaultSelected.push(HotelPreferences.ONLY_COMMISSIONABLE);
        payload.searchObj.doShowCommissionableRatesOnly = true;
      }
      if (travelAgency.filterSettings.showRefundableHotelRatesOnly) {
        defaultSelected.push(HotelPreferences.ONLY_REFUNDABLE_RATES);
        payload.searchObj.showRefundableHotelRatesOnly = true;
      }
      if (travelAgency.filterSettings.doShowAvailableHotelsOnly) {
        defaultSelected.push(HotelPreferences.ONLY_AVAILABLE_HOTELS);
        payload.searchObj.doShowAvailableHotelsOnly = true;
      }
      if (defaultSelected.length) {
        if (isHotelTripSearch) {
          dispatch(setHotelTripSearchInputs({ ...payload.searchObj }));
        } else {
          dispatch(updateHotelSearchObj(payload));
        }
      }
    }
    // Don't add hotelSearchObj as dependency (it'll cause infinite renders)
    // Since this effect is for setting default values, thats why its alright
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, doReDesignRateCodesFilter, isHotelTripSearch, travelAgency, tripId]);

  return (!doReDesignRateCodesFilter && preferences.length) ? (
    <Filter
      label={agentPreferencesLabel}
      menuWidthClass="w-full md:w-max"
      variant={variant}
      filterMaxWidthClass={isSmallScreen ? 'w-full' : 'max-w-72'}
      isFilterEmpty={isAgentPreferencesEmpty}
      handleClear={handlePreferencesClear}
      widthInside="max-w-90"
      bgClassName={!isHotelTripSearch ? '' : filterBgColor}
      dropdownAligning={isSmallScreen ? 'right' : 'left'}
    >
      <Dropdown.Item>
        <FormControl
          className="grid col-span-5 grid-cols-2 gap-y-2 w-full items-center"
          component="fieldset"
        >
          <FormGroup classes={formClasses} className={clsx('overflow-x-auto max-h-1')}>
            <RateCodeConfigPreferences
              onChange={handlePreferencesChange}
              preferences={preferences}
              selectedPreferences={selectedPreferences}
            />
            {doTakeHotelContentSourceInput && <ContentSourceFilter tripId={tripId} />}
          </FormGroup>
        </FormControl>
      </Dropdown.Item>
    </Filter>
  ) : <></>;
};

AgentPreferencesFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterBgColor: PropTypes.string,
};

AgentPreferencesFilter.defaultProps = {
  tripId: undefined,
  variant: 'outline',
  filterBgColor: undefined,
};
export default AgentPreferencesFilter;
